import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import TimeWeekStatusPill from './TimeWeekStatusPill';

import { parseIsoDate } from 'Helpers/dateFormat';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import SelectInput from 'CommonComponents/SelectInput';

import useDropdown from 'Hooks/useDropdown';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronLeftIcon from 'Assets/icons/chevron-left.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const isWeekend = (date) => [0, 6].includes(parseIsoDate(date)?.getDay());

/**
 * The month to open on, held inside the months the server offers — this one
 * and the twelve before it, newest first — so the select never shows a
 * different month from the weeks beneath it. A future week opens on this
 * month, an older one on the earliest.
 */
function monthInRange(month, months) {
    if (months.length === 0) return month;
    if (month > months[0].value) return months[0].value;
    if (month < months[months.length - 1].value) return months[months.length - 1].value;

    return month;
}

/**
 * The week label as a button that opens a month of weeks, one row each with its
 * status. The month select offers this month and the twelve before it, so any
 * week in the past year is three clicks away. A week that has not started yet
 * is shown but not picked from here — the next-week arrow still reaches it.
 */
export default function TimeWeekPicker({ calendarUrl, week, disabled, onPick }) {
    const { triggerRef, dropdownRef, isOpen, toggle, close } = useDropdown();
    const [month, setMonth] = useState(null);
    const [calendar, setCalendar] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    // Only the newest month may land: the arrows stay live while one loads.
    const readSeq = useRef(0);
    // Set on opening and cleared once focus has moved into the panel, which has
    // to wait for the month's weeks to arrive.
    const focusOnLoad = useRef(false);

    const focusTrigger = useCallback(() => triggerRef.current?.querySelector('button')?.focus(), [triggerRef]);

    const open = () => {
        if (!isOpen) {
            setMonth(week.start.slice(0, 7));
            focusOnLoad.current = true;
        }

        toggle();
    };

    // Escape closes from anywhere while open, not only from inside the panel.
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                close();
                focusTrigger();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [close, focusTrigger, isOpen]);

    useEffect(() => {
        if (!isOpen || !month) {
            return;
        }

        const sequence = ++readSeq.current;
        setLoading(true);
        setError(null);

        axios
            .get(calendarUrl, { params: { month } })
            .then(({ data }) => {
                if (sequence !== readSeq.current) {
                    return;
                }

                // A week outside the offered months loads once, then moves to
                // the nearest month the server offers. Clamped against the
                // server's list rather than the browser's today, which can sit
                // in a different month across a timezone boundary.
                const clamped = monthInRange(month, data.data.months ?? []);

                if (clamped !== month) {
                    setMonth(clamped);

                    return;
                }

                setCalendar(data.data);
            })
            .catch(() => {
                if (sequence === readSeq.current) {
                    setError($t('Something went wrong'));
                }
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setLoading(false);
                }
            });
    }, [calendarUrl, isOpen, month]);

    const pick = useCallback(
        (start) => {
            close();
            onPick(start);
            focusTrigger();
        },
        [close, focusTrigger, onPick],
    );

    const weeks = calendar?.month?.value === month ? calendar.weeks : [];
    const firstDays = weeks[0]?.days ?? [];

    // Into the panel once it has something in it: the open week's row when this
    // month holds it, otherwise the month select.
    useEffect(() => {
        if (!isOpen || !focusOnLoad.current || (weeks.length === 0 && !error)) {
            return;
        }

        focusOnLoad.current = false;

        const panel = dropdownRef.current;
        (panel?.querySelector('tr[aria-current="true"][tabindex]') ?? panel?.querySelector('select'))?.focus();
    }, [dropdownRef, error, isOpen, weeks]);

    const weekText = `${week.label} · ${$t('week {{number}}', { number: week.number })}`;

    return (
        <>
            {/* The ref sits on a wrapper: Button does not forward refs. */}
            <span ref={triggerRef} className="tw-inline-flex">
                <Button
                    variant="secondary"
                    small
                    disabled={disabled}
                    onClick={open}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-label={$t('Choose a week, currently {{week}}', { week: weekText })}
                >
                    <span className="tw-font-semibold">{weekText}</span>
                    <ChevronDownIcon className="tw-h-3 tw-w-3" />
                </Button>
            </span>

            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        role="dialog"
                        aria-label={$t('Choose a week')}
                        className="tw-w-[34rem] tw-max-w-[calc(100vw-1rem)] tw-overflow-auto tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-4 tw-text-start tw-shadow-primary"
                    >
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                            <Button
                                variant="secondary"
                                small
                                disabled={!calendar?.previous || calendar?.month?.value !== month}
                                onClick={() => setMonth(calendar.previous)}
                                aria-label={$t('Previous month')}
                            >
                                <ChevronLeftIcon className="tw-h-3 tw-w-3" />
                            </Button>
                            <div className="tw-flex-1">
                                <SelectInput
                                    name="time_week_picker_month"
                                    aria-label={$t('Month')}
                                    value={month ?? ''}
                                    onChange={(event) => setMonth(event.target.value)}
                                >
                                    {(calendar?.months ?? []).map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </SelectInput>
                            </div>
                            <Button variant="secondary" small disabled={!calendar} onClick={() => pick(calendar.current_week)}>
                                {$t('This week')}
                            </Button>
                            <Button
                                variant="secondary"
                                small
                                disabled={!calendar?.next || calendar?.month?.value !== month}
                                onClick={() => setMonth(calendar.next)}
                                aria-label={$t('Next month')}
                            >
                                <ChevronRightIcon className="tw-h-3 tw-w-3" />
                            </Button>
                        </div>

                        {!!error && <Alert type="error">{error}</Alert>}

                        {loading && weeks.length === 0 && <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">{$t('Loading')}…</p>}

                        {weeks.length > 0 && (
                            <table className="tw-w-full tw-text-sm">
                                <thead>
                                    <tr className="tw-border-b tw-border-grey tw-text-xs tw-text-charcoal-50">
                                        <th className="tw-py-2 tw-font-medium tw-text-start">{$t('Week')}</th>
                                        {firstDays.map((day) => (
                                            <th key={day.date} className="tw-py-2 tw-font-medium tw-text-center">
                                                {$t(DAY_ABBR[parseIsoDate(day.date).getDay()])}
                                            </th>
                                        ))}
                                        <th className="tw-py-2 tw-font-medium tw-text-end">{$t('Status')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {weeks.map((row) => {
                                        const selectable = row.status !== 'future';
                                        const selected = row.start === week.start;

                                        return (
                                            <tr
                                                key={row.start}
                                                tabIndex={selectable ? 0 : undefined}
                                                aria-current={selected ? 'true' : undefined}
                                                aria-disabled={!selectable || undefined}
                                                aria-label={`${$t('week {{number}}', { number: row.number })}, ${row.label}`}
                                                onClick={() => selectable && pick(row.start)}
                                                onKeyDown={(event) => {
                                                    if (selectable && (event.key === 'Enter' || event.key === ' ')) {
                                                        event.preventDefault();
                                                        pick(row.start);
                                                    }
                                                }}
                                                className={classNames('tw-border-b tw-border-grey last:tw-border-b-0', {
                                                    'tw-cursor-pointer hover:tw-bg-background': selectable && !selected,
                                                    'tw-bg-blue-10': selected,
                                                    'tw-cursor-not-allowed': !selectable,
                                                })}
                                            >
                                                <td className={classNames('tw-py-2 tw-font-semibold', selected ? 'tw-text-blue' : 'tw-text-charcoal-50')}>
                                                    {$t('W{{number}}', { number: row.number })}
                                                </td>
                                                {row.days.map((day) => (
                                                    <td key={day.date} className="tw-py-2 tw-text-center">
                                                        <span
                                                            className={classNames(
                                                                'tw-inline-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full',
                                                                {
                                                                    'tw-border-2 tw-border-blue tw-font-semibold tw-text-blue': day.today,
                                                                    'tw-text-charcoal-50': !day.today && (!day.in_month || isWeekend(day.date)),
                                                                    'tw-text-charcoal': !day.today && day.in_month && !isWeekend(day.date),
                                                                },
                                                            )}
                                                        >
                                                            {day.day}
                                                        </span>
                                                    </td>
                                                ))}
                                                <td className="tw-py-2 tw-text-end">
                                                    <TimeWeekStatusPill status={row.status} size="sm" />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}

                        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mt-3 tw-pt-3 tw-border-t tw-border-grey">
                            <div className="tw-flex tw-flex-wrap tw-gap-2">
                                <TimeWeekStatusPill status="submitted" size="sm" />
                                <TimeWeekStatusPill status="draft" size="sm" />
                                <TimeWeekStatusPill status="empty" size="sm" />
                            </div>
                            <span className="tw-text-xs tw-text-charcoal-50">{$t('Select a week to open it')}</span>
                        </div>
                    </div>,
                    document.body,
                )}
        </>
    );
}
